import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertTriangle, ShieldAlert, ShieldCheck, Shield, Eye, Pause, Snowflake,
  XCircle, ArrowUpRight, CheckCircle2, Clock, User, Building2, CreditCard,
  Banknote, Activity, TrendingUp, BarChart3, Loader2, RefreshCw, ChevronRight,
  CircleDot, Layers, DollarSign, Target, Zap
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fraud`;

const SEV_CONFIG = {
  critical: { color: "#dc2626", bg: "bg-red-50", text: "text-red-700", border: "border-red-200", icon: ShieldAlert, label: "Critical" },
  high: { color: "#ea580c", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", icon: AlertTriangle, label: "High" },
  medium: { color: "#ca8a04", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: Shield, label: "Medium" },
  low: { color: "#6b7280", bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", icon: ShieldCheck, label: "Low" },
};

const CHANNEL_CONFIG = {
  payroll: { color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-700", icon: Banknote, label: "Payroll" },
  bill_pay: { color: "#8b5cf6", bg: "bg-violet-50", text: "text-violet-700", icon: CreditCard, label: "Bill Pay" },
  cards: { color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-700", icon: CreditCard, label: "Cards" },
  cross_channel: { color: "#dc2626", bg: "bg-red-50", text: "text-red-700", icon: Layers, label: "Cross-Channel" },
};

const STATUS_CONFIG = {
  new: { bg: "bg-blue-50", text: "text-blue-700", label: "New" },
  in_progress: { bg: "bg-amber-50", text: "text-amber-700", label: "In Progress" },
  confirmed: { bg: "bg-red-50", text: "text-red-700", label: "Confirmed" },
  dismissed: { bg: "bg-gray-100", text: "text-gray-500", label: "Dismissed" },
  escalated: { bg: "bg-purple-50", text: "text-purple-700", label: "Escalated" },
};

const fmtCurrency = (v) => {
  if (v == null) return "-";
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const timeAgo = (iso) => {
  if (!iso) return "-";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

// ─── SUMMARY BAR ───────────────────────────────────────────────
function SummaryBar({ summary, loading }) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse"><CardContent className="p-4"><div className="h-10 bg-gray-100 rounded" /></CardContent></Card>
        ))}
      </div>
    );
  }
  const cards = [
    { label: "Open Alerts", value: summary.open_alerts, icon: AlertTriangle, color: "#dc2626", sub: `${summary.critical} critical, ${summary.high} high` },
    { label: "Amount at Risk", value: fmtCurrency(summary.total_at_risk), icon: DollarSign, color: "#ea580c", sub: `${summary.alerts_today} new today` },
    { label: "Losses Prevented", value: fmtCurrency(summary.losses_prevented_mtd), icon: ShieldCheck, color: "#16a34a", sub: "Month to date" },
    { label: "Losses Incurred", value: fmtCurrency(summary.losses_incurred_mtd), icon: TrendingUp, color: "#dc2626", sub: "Month to date" },
    { label: "Detection Rate", value: `${summary.detection_rate}%`, icon: Target, color: "#502D3C", sub: "True positive rate" },
    { label: "Fraud Rate (7d)", value: `${(summary.fraud_rate_7d * 100).toFixed(1)}%`, icon: Activity, color: "#ca8a04", sub: `Top: ${CHANNEL_CONFIG[summary.top_channel]?.label || summary.top_channel}` },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="fraud-summary-bar">
      {cards.map((c, i) => (
        <Card key={i} className="border border-gray-200 shadow-none hover:shadow-sm transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{c.label}</span>
              <c.icon className="h-4 w-4" style={{ color: c.color }} />
            </div>
            <div className="text-xl font-bold text-gray-900">{c.value}</div>
            <div className="text-xs text-gray-400 mt-1">{c.sub}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── SEVERITY BADGE ────────────────────────────────────────────
function SeverityBadge({ severity }) {
  const cfg = SEV_CONFIG[severity] || SEV_CONFIG.low;
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold", cfg.bg, cfg.text)} data-testid={`severity-badge-${severity}`}>
      <cfg.icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function ChannelBadge({ channel }) {
  const cfg = CHANNEL_CONFIG[channel] || CHANNEL_CONFIG.cards;
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium", cfg.bg, cfg.text)}>
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  return <span className={cn("inline-flex items-center px-2 py-0.5 rounded text-xs font-medium", cfg.bg, cfg.text)}>{cfg.label}</span>;
}

// ─── ALERT QUEUE ───────────────────────────────────────────────
function AlertQueue({ alerts, loading, filters, setFilters, onSelect, selectedId }) {
  return (
    <Card className="border border-gray-200 shadow-none" data-testid="alert-queue">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-gray-900">Alert Queue</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filters.channel} onValueChange={(v) => setFilters((p) => ({ ...p, channel: v }))}>
              <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="filter-channel">
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="payroll">Payroll</SelectItem>
                <SelectItem value="bill_pay">Bill Pay</SelectItem>
                <SelectItem value="cards">Cards</SelectItem>
                <SelectItem value="cross_channel">Cross-Channel</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity} onValueChange={(v) => setFilters((p) => ({ ...p, severity: v }))}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="filter-severity">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No alerts match filters</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead className="text-xs w-[70px]">Severity</TableHead>
                  <TableHead className="text-xs w-[80px]">ID</TableHead>
                  <TableHead className="text-xs">Fraud Type</TableHead>
                  <TableHead className="text-xs">Company</TableHead>
                  <TableHead className="text-xs w-[90px]">Channel</TableHead>
                  <TableHead className="text-xs w-[90px] text-right">$ at Risk</TableHead>
                  <TableHead className="text-xs w-[60px] text-center">Score</TableHead>
                  <TableHead className="text-xs w-[90px]">Status</TableHead>
                  <TableHead className="text-xs w-[100px]">Assignee</TableHead>
                  <TableHead className="text-xs w-[70px]">Time</TableHead>
                  <TableHead className="text-xs w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((a) => (
                  <TableRow
                    key={a.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      selectedId === a.id ? "bg-[#502D3C]/5 border-l-2 border-l-[#502D3C]" : "hover:bg-gray-50",
                      a.severity === "critical" && a.status === "new" && "bg-red-50/40"
                    )}
                    onClick={() => onSelect(a.id)}
                    data-testid={`alert-row-${a.id}`}
                  >
                    <TableCell><SeverityBadge severity={a.severity} /></TableCell>
                    <TableCell className="text-xs font-mono text-gray-500">{a.id}</TableCell>
                    <TableCell className="text-sm font-medium text-gray-900 max-w-[180px] truncate">{a.fraud_type}</TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[150px] truncate">{a.company_name}</TableCell>
                    <TableCell><ChannelBadge channel={a.channel} /></TableCell>
                    <TableCell className="text-sm font-semibold text-right" style={{ color: a.amount_at_risk > 50000 ? "#dc2626" : a.amount_at_risk > 10000 ? "#ea580c" : "#374151" }}>
                      {fmtCurrency(a.amount_at_risk)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={cn(
                        "inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold",
                        a.fraud_score >= 70 ? "bg-red-100 text-red-700" : a.fraud_score >= 40 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-600"
                      )}>{a.fraud_score}</span>
                    </TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                    <TableCell className="text-xs text-gray-500">{a.assignee || <span className="text-gray-300">Unassigned</span>}</TableCell>
                    <TableCell className="text-xs text-gray-400">{timeAgo(a.created_at)}</TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-gray-300" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── INVESTIGATION PANEL ───────────────────────────────────────
function InvestigationPanel({ alert, open, onClose, onAction }) {
  const [actionLoading, setActionLoading] = useState(null);

  if (!alert) return null;

  const handleAction = async (action) => {
    setActionLoading(action);
    await onAction(alert.id, action);
    setActionLoading(null);
  };

  const channelIcon = (ch) => {
    if (ch === "payroll") return <Banknote className="h-3.5 w-3.5 text-blue-500" />;
    if (ch === "bill_pay") return <CreditCard className="h-3.5 w-3.5 text-violet-500" />;
    if (ch === "cards") return <CreditCard className="h-3.5 w-3.5 text-amber-500" />;
    if (ch === "identity") return <User className="h-3.5 w-3.5 text-gray-500" />;
    return <Layers className="h-3.5 w-3.5 text-red-500" />;
  };

  const sevColors = (sev) => {
    if (sev === "critical") return "border-red-400 bg-red-50";
    if (sev === "high") return "border-orange-400 bg-orange-50";
    if (sev === "medium") return "border-amber-300 bg-amber-50";
    return "border-gray-200 bg-gray-50";
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-[600px] p-0 overflow-hidden" data-testid="investigation-panel">
        <ScrollArea className="h-full">
          <div className="p-6">
            <SheetHeader className="mb-4">
              <div className="flex items-center gap-3">
                <SeverityBadge severity={alert.severity} />
                <span className="text-sm font-mono text-gray-400">{alert.id}</span>
                <StatusBadge status={alert.status} />
              </div>
              <SheetTitle className="text-lg font-semibold text-gray-900 mt-2">{alert.fraud_type}</SheetTitle>
              <p className="text-sm text-gray-500 mt-1">{alert.company?.name} &middot; {CHANNEL_CONFIG[alert.channel]?.label}</p>
            </SheetHeader>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-5 border border-gray-100">
              <p className="text-sm text-gray-700 leading-relaxed">{alert.summary}</p>
            </div>

            {/* Key Metrics Row */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">Amount at Risk</div>
                <div className="text-lg font-bold" style={{ color: alert.amount_at_risk > 50000 ? "#dc2626" : "#ea580c" }}>
                  {fmtCurrency(alert.amount_at_risk)}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">Fraud Score</div>
                <div className={cn("text-lg font-bold", alert.fraud_score >= 70 ? "text-red-600" : alert.fraud_score >= 40 ? "text-orange-600" : "text-gray-700")}>
                  {alert.fraud_score}/100
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">Channels</div>
                <div className="text-lg font-bold text-gray-900">{alert.channels_affected?.length || 1}</div>
              </div>
            </div>

            {/* Entity Info */}
            {alert.entity && (
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Entity</h4>
                <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
                  <div className="w-9 h-9 rounded-full bg-[#502D3C]/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-[#502D3C]" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{alert.entity.name}</div>
                    <div className="text-xs text-gray-500">
                      {alert.entity.type?.replace(/_/g, " ")} &middot; {alert.entity.bank} ****{alert.entity.bank_last4}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Signals */}
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Signals ({alert.signals?.length || 0})
              </h4>
              <div className="space-y-2">
                {alert.signals?.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-3">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                      s.score >= 7 ? "bg-red-100 text-red-700" : s.score >= 5 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-600"
                    )}>{s.score}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900">{s.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {channelIcon(s.channel)}
                        <span className="text-xs text-gray-400 capitalize">{s.confidence} confidence</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Timeline ({alert.timeline?.length || 0} events)
              </h4>
              <div className="relative pl-6 space-y-0">
                {alert.timeline?.map((t, i) => (
                  <div key={i} className="relative pb-4 last:pb-0">
                    {i < alert.timeline.length - 1 && (
                      <div className="absolute left-[-16px] top-3 bottom-0 w-px bg-gray-200" />
                    )}
                    <div className={cn("absolute left-[-20px] top-1 w-2 h-2 rounded-full border-2", sevColors(t.severity))} />
                    <div className="flex items-start gap-2">
                      {channelIcon(t.channel)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800">{t.event}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{new Date(t.time).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommended Action */}
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Recommended Action</h4>
              <p className="text-sm text-amber-900">{alert.recommended_action}</p>
            </div>

            {/* Cross-Channel & Time Multipliers */}
            {(alert.cross_channel_multiplier > 1 || alert.time_multiplier > 1) && (
              <div className="mb-5 flex gap-3">
                {alert.cross_channel_multiplier > 1 && (
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="text-xs text-red-600 font-medium">Cross-Channel Multiplier</div>
                    <div className="text-lg font-bold text-red-700">{alert.cross_channel_multiplier}x</div>
                  </div>
                )}
                {alert.time_multiplier > 1 && (
                  <div className="flex-1 bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="text-xs text-orange-600 font-medium">Time Multiplier</div>
                    <div className="text-lg font-bold text-orange-700">{alert.time_multiplier}x</div>
                  </div>
                )}
              </div>
            )}

            <Separator className="my-4" />

            {/* Triage Actions */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Triage Actions</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline" size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => handleAction("hold")}
                  disabled={!!actionLoading}
                  data-testid="action-hold"
                >
                  {actionLoading === "hold" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pause className="h-4 w-4 mr-1" />}
                  Hold Payment
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => handleAction("freeze")}
                  disabled={!!actionLoading}
                  data-testid="action-freeze"
                >
                  {actionLoading === "freeze" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Snowflake className="h-4 w-4 mr-1" />}
                  Freeze Account
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => handleAction("confirm")}
                  disabled={!!actionLoading}
                  data-testid="action-confirm"
                >
                  {actionLoading === "confirm" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Confirm Fraud
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => handleAction("escalate")}
                  disabled={!!actionLoading}
                  data-testid="action-escalate"
                >
                  {actionLoading === "escalate" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowUpRight className="h-4 w-4 mr-1" />}
                  Escalate
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 col-span-2"
                  onClick={() => handleAction("dismiss")}
                  disabled={!!actionLoading}
                  data-testid="action-dismiss"
                >
                  {actionLoading === "dismiss" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                  Dismiss Alert
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ─── ANALYTICS PANEL ───────────────────────────────────────────
function AnalyticsPanel({ analytics, loading }) {
  if (loading || !analytics) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }

  const CHANNEL_COLORS_CHART = { payroll: "#3b82f6", bill_pay: "#8b5cf6", cards: "#f59e0b", cross_channel: "#dc2626" };
  const TYPE_COLORS = ["#502D3C", "#dc2626", "#ea580c", "#ca8a04", "#3b82f6", "#8b5cf6", "#16a34a", "#0891b2", "#6366f1", "#f59e0b"];

  // Heatmap: pivot to rows by day
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const heatmapByDay = {};
  analytics.heatmap.forEach((h) => {
    if (!heatmapByDay[h.day]) heatmapByDay[h.day] = {};
    heatmapByDay[h.day][h.hour] = h.count;
  });
  const maxHeat = Math.max(...analytics.heatmap.map((h) => h.count), 1);

  const heatCellColor = (count) => {
    const ratio = count / maxHeat;
    if (ratio === 0) return "bg-gray-50";
    if (ratio < 0.25) return "bg-green-100";
    if (ratio < 0.5) return "bg-amber-100";
    if (ratio < 0.75) return "bg-orange-200";
    return "bg-red-300";
  };

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      {/* Fraud by Channel Over Time */}
      <Card className="border border-gray-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-900">Fraud Alerts by Channel (13 Weeks)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={analytics.fraud_by_channel} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="payroll" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} name="Payroll" />
              <Area type="monotone" dataKey="bill_pay" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.4} name="Bill Pay" />
              <Area type="monotone" dataKey="cards" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.4} name="Cards" />
              <Area type="monotone" dataKey="cross_channel" stackId="1" stroke="#dc2626" fill="#dc2626" fillOpacity={0.4} name="Cross-Channel" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Fraud by Type */}
        <Card className="border border-gray-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900">Fraud by Type (90 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.fraud_by_type} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCurrency(v)} />
                <YAxis type="category" dataKey="type" tick={{ fontSize: 10 }} width={150} />
                <RTooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
                <Bar dataKey="amount" radius={[0, 4, 4, 0]} barSize={18}>
                  {analytics.fraud_by_type.map((_, i) => (
                    <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Time-of-Day Heatmap */}
        <Card className="border border-gray-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-gray-900">Activity Heatmap (Day x Hour)</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-center" data-testid="heatmap-table">
              <thead>
                <tr>
                  <th className="text-xs text-gray-500 font-medium p-1 w-10"></th>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <th key={h} className="text-[10px] text-gray-400 font-normal p-0.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {days.map((day) => (
                  <tr key={day}>
                    <td className="text-xs text-gray-500 font-medium p-1 text-left">{day}</td>
                    {Array.from({ length: 24 }).map((_, h) => {
                      const count = heatmapByDay[day]?.[h] || 0;
                      return (
                        <TooltipProvider key={h}>
                          <Tooltip delayDuration={100}>
                            <TooltipTrigger asChild>
                              <td className={cn("w-4 h-4 rounded-sm m-0.5", heatCellColor(count))} />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {day} {h}:00 &mdash; {count} alerts
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2 mt-3 justify-end">
              <span className="text-[10px] text-gray-400">Less</span>
              {["bg-gray-50", "bg-green-100", "bg-amber-100", "bg-orange-200", "bg-red-300"].map((c, i) => (
                <div key={i} className={cn("w-3 h-3 rounded-sm", c)} />
              ))}
              <span className="text-[10px] text-gray-400">More</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ────────────────────────────────────────────
export default function FraudDashboard() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [filters, setFilters] = useState({ channel: "all", severity: "all", status: "all" });
  const [activeTab, setActiveTab] = useState("queue");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.channel !== "all") params.append("channel", filters.channel);
      if (filters.severity !== "all") params.append("severity", filters.severity);
      if (filters.status !== "all") params.append("status", filters.status);
      const [sumRes, alertRes] = await Promise.all([
        axios.get(`${API}/summary`),
        axios.get(`${API}/alerts?${params.toString()}`),
      ]);
      setSummary(sumRes.data);
      setAlerts(alertRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/analytics`);
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
    finally { setAnalyticsLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleSelectAlert = async (id) => {
    try {
      const res = await axios.get(`${API}/alerts/${id}`);
      setSelectedAlert(res.data);
      setSheetOpen(true);
    } catch (e) { console.error(e); }
  };

  const handleAction = async (alertId, action) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/triage`, { action });
      fetchData();
      const res = await axios.get(`${API}/alerts/${alertId}`);
      setSelectedAlert(res.data);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 max-w-[1440px] mx-auto" data-testid="fraud-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="fraud-dashboard-title">Fraud Risk Intelligence</h1>
          <p className="text-sm text-gray-500 mt-0.5">Real-time fraud signals across payroll, bill pay, and corporate cards</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchData(); fetchAnalytics(); }} className="gap-1.5" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Panel 1: Summary Bar */}
      <div className="mb-5">
        <SummaryBar summary={summary} loading={loading} />
      </div>

      {/* Tabs: Queue vs Analytics */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 bg-gray-100">
          <TabsTrigger value="queue" className="text-sm gap-1.5" data-testid="tab-queue">
            <AlertTriangle className="h-4 w-4" /> Alert Queue ({alerts.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="text-sm gap-1.5" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4" /> Analytics & Trends
          </TabsTrigger>
        </TabsList>

        {/* Panel 2: Alert Queue */}
        <TabsContent value="queue">
          <AlertQueue
            alerts={alerts}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSelect={handleSelectAlert}
            selectedId={selectedAlert?.id}
          />
        </TabsContent>

        {/* Panel 4: Analytics */}
        <TabsContent value="analytics">
          <AnalyticsPanel analytics={analytics} loading={analyticsLoading} />
        </TabsContent>
      </Tabs>

      {/* Panel 3: Investigation Panel (Sheet) */}
      <InvestigationPanel
        alert={selectedAlert}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onAction={handleAction}
      />
    </div>
  );
}
